/**
 * Lightweight analytics beacon for HireBroker web frontend.
 * Sends pageviews & events to admin API: POST /api/analytics/track
 */

const API_BASE = 'https://app.hirebroker.co/api';
const SESSION_KEY = 'hb_web_sid';
const QUEUE_KEY = 'hb_web_aq';

function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, '');
  }
  return `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

export function getSessionId() {
  try {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = uuid().slice(0, 32);
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return uuid().slice(0, 32);
  }
}

function readUtm() {
  try {
    const p = new URLSearchParams(window.location.search);
    return {
      utm_source: p.get('utm_source') || undefined,
      utm_medium: p.get('utm_medium') || undefined,
      utm_campaign: p.get('utm_campaign') || undefined,
    };
  } catch {
    return {};
  }
}

function getCustomerId() {
  try {
    // Prefer redux-persisted auth if present
    const raw = localStorage.getItem('persist:root') || localStorage.getItem('auth');
    if (!raw) return undefined;
    const parsed = JSON.parse(raw);
    const auth = typeof parsed?.auth === 'string' ? JSON.parse(parsed.auth) : parsed?.auth || parsed;
    const id = auth?.customer_id || auth?.user?.id || auth?.user?.customer_id;
    return id ? Number(id) : undefined;
  } catch {
    return undefined;
  }
}

async function postEvent(payload) {
  try {
    await fetch(`${API_BASE}/analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
      mode: 'cors',
    });
  } catch {
    // best-effort; queue for later
    try {
      const q = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
      q.push(payload);
      localStorage.setItem(QUEUE_KEY, JSON.stringify(q.slice(-40)));
    } catch {
      /* ignore */
    }
  }
}

/**
 * Track a page view (call on route change).
 */
export function trackPageView(pathname, pageTitle) {
  if (typeof window === 'undefined') return;
  const utm = readUtm();
  postEvent({
    session_id: getSessionId(),
    event_type: 'pageview',
    path: pathname || window.location.pathname || '/',
    page_title: pageTitle || document.title || undefined,
    referrer: document.referrer || undefined,
    customer_id: getCustomerId(),
    ...utm,
  });
}

/**
 * Track a named action (search, contact, signup, etc.).
 * @param {string} eventName e.g. 'property_view', 'contact_agent', 'search'
 * @param {object} meta optional metadata
 * @param {string} eventType pageview|click|search|lead|signup|login|custom
 */
export function trackEvent(eventName, meta = {}, eventType = 'custom') {
  if (typeof window === 'undefined') return;
  postEvent({
    session_id: getSessionId(),
    event_type: eventType,
    event_name: eventName,
    path: window.location.pathname || '/',
    page_title: document.title || undefined,
    referrer: document.referrer || undefined,
    customer_id: getCustomerId(),
    meta,
    ...readUtm(),
  });
}

/** Flush any offline queue (call once on app load). */
export function flushAnalyticsQueue() {
  try {
    const q = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
    if (!Array.isArray(q) || !q.length) return;
    localStorage.removeItem(QUEUE_KEY);
    q.slice(0, 25).forEach((payload) => postEvent(payload));
  } catch {
    /* ignore */
  }
}
